import React, { useState } from 'react';
import { SessionService } from '../services/sessionService';
import { normalizeJoinInput, extractSessionId } from '../utils/joinInput';
import './JoinPanel.css';

/**
 * Single input that accepts either a resume code (BADGER-42) or a pasted
 * share link. A link is adopted through the same query-string path
 * App's session orchestration already handles for `?session=` links —
 * including its live-session confirm dialog — so that logic lives in one
 * place. A code has no such endpoint-driven path, so it keeps its own
 * resumeByCode + confirm flow.
 */
const JoinPanel: React.FC = () => {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleBlur = () => {
    setValue(v => normalizeJoinInput(v));
  };

  const handleSubmit = async () => {
    const trimmed = value.trim();
    if (!trimmed) return;

    const sessionId = extractSessionId(trimmed);
    if (sessionId) {
      // Reuse App's resume-link adoption path (confirm dialog included).
      window.location.href = `/?session=${sessionId}`;
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const session = await SessionService.resumeByCode(trimmed);

      const localSessionId = SessionService.getSessionId();
      if (localSessionId && localSessionId !== session.id) {
        const localIsLive = await SessionService.validateSession(localSessionId);
        if (localIsLive) {
          const confirmed = window.confirm(
            'This device already has a crawl in progress. Resuming will replace it. Continue?'
          );
          if (!confirmed) {
            setIsSubmitting(false);
            return;
          }
        }
      }

      SessionService.setSessionId(session.id);
      // Full navigation so App re-initialises with the adopted session
      window.location.href = '/';
    } catch (err) {
      console.error('Error resuming session:', err);
      setError("Couldn't find a crawl with that code or link. Check it and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="join-panel">
      <h2 className="join-panel-title">Resume/Join a crawl</h2>
      <p className="join-panel-hint">Enter a code for an ongoing crawl to join or resume it.</p>
      <div className="join-panel-row">
        <input
          type="text"
          className="join-panel-input"
          data-testid="join-input"
          placeholder="BADGER-42, or paste a link"
          aria-label="Resume code or share link"
          value={value}
          onChange={e => setValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          disabled={isSubmitting}
        />
        <button
          className="join-panel-button"
          data-testid="join-submit"
          onClick={handleSubmit}
          disabled={isSubmitting || !value.trim()}
        >
          {isSubmitting ? 'Joining…' : 'Join'}
        </button>
      </div>
      {error && <p className="join-panel-error">{error}</p>}
    </div>
  );
};

export default JoinPanel;
